import SimpleBar from 'simplebar';
import Helpers from './helpers';

declare var Mustache: any;

export default class ScrollCarousel {

	private containerElement: HTMLElement;
	private simpleBarInstance: any;
	private carouselBar: HTMLElement;
	private lazyLoadData: any;
	private lazyLoadDataKey: any;
	private lazyLoadEnabled = false;
	private lazyLoadTemplate: string;
	private isDown: boolean = false;
	private startX: number = 0;
	private scrollLeft: number = 0;
	private isDragging: boolean = false;
	private enableDesktop: boolean = true;
	private enableMobile: boolean = true;
	private blockID: string;

	constructor(containerElement, lazyLoadData?: any, lazyTemplate?: string, lazyLoadDataKey?: string) {
		if (!containerElement) {
			return;
		}

		if (containerElement.classList && containerElement.classList.contains('js-scroll-carousel-done')) {
			return;
		}
		this.containerElement = containerElement;
		this.lazyLoadData = lazyLoadData || null
		this.lazyLoadTemplate = lazyTemplate || null;
		this.lazyLoadDataKey = lazyLoadDataKey || null;
		this.lazyLoadEnabled = this.lazyLoadData !== null && this.lazyLoadTemplate !== null && this.lazyLoadDataKey !== null;
		const potentialBlockID = this.containerElement.closest('[block-id]') as HTMLElement;

		if (potentialBlockID) {
			this.blockID = this.containerElement.closest('[block-id]').getAttribute('block-id');
		}
		
		if (this.lazyLoadEnabled) {
			this.setLazyloadPlaceholder();
		}

		if (containerElement.classList.contains('js-scroll-carousel--desktop-only') && !containerElement.classList.contains('js-scroll-carousel--mobile-only')) {
			this.enableMobile = false;
		}

		if (containerElement.classList.contains('js-scroll-carousel--mobile-only') && !containerElement.classList.contains('js-scroll-carousel--desktop-only')) {
			this.enableDesktop = false;
		}

		this.containerElement.classList.add('c-carousel--scroll-layout-decided');
		this.setupCarousel();

		if (this.deviceHasHover()) {
			this.addDragFunctionality();
		}
		
	}

	private deviceHasHover() {
		return window.matchMedia('(hover: hover)').matches;
	}

	private isDesktop() {
		return matchMedia('(min-width: 980px)').matches;
	}


	private isMobile() {
		return matchMedia('(max-width: 979px)').matches;
	}


	private setupCarousel() {
		if (this.isMobile() && !this.enableMobile) {
			this.containerElement.classList.add('has-loaded');
			return;
		}

		if (this.isDesktop() && !this.enableDesktop) {
			this.containerElement.classList.add('has-loaded');
			return;
		}

		if (this.enableDesktop) {
			this.containerElement.classList.add('c-carousel--scroll-desktop-enabled');
		}

		if (this.enableMobile) {
			this.containerElement.classList.add('c-carousel--scroll-mobile-enabled');
		}

		this.containerElement.classList.add('c-carousel--scroll');

		this.containerElement.classList.add('js-scroll-carousel-done');

		this.simpleBarInstance = new SimpleBar(this.containerElement, {
			autoHide: false
		});

		this.carouselBar = this.containerElement.querySelector('.simplebar-horizontal') as HTMLElement;

		this.containerElement.classList.add('has-loaded');
		const sectionContainer = this.containerElement.closest('.c-section--has-scroll');
		if (sectionContainer) {
			sectionContainer.classList.add('has-loaded');
		}

		if (this.userHasGoneBackOrReloaded()) {
			this.checkIfShouldScrollToElement();
		}

		this.simpleBarInstance.getScrollElement().addEventListener('scroll', () => this.onCarouselScroll());

		this.addArrows();

	}

	private userHasGoneBackOrReloaded() {
		const navigationType = (window.performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming).type;
		return navigationType === 'reload' || navigationType === 'back_forward';
	}

	private checkIfShouldScrollToElement() {
		const blockState = this.getBlockState();
		if (blockState) {
			setTimeout(() => {
				this.simpleBarInstance.getScrollElement().scrollTo({
					left: blockState.scrollLeft,
					top: 0,
					behavior: 'auto'
				});
				this.containerElement.scrollIntoView();
				sessionStorage.removeItem(`carousel-${this.blockID}-state`);
			}, 250);
		}

	}

	private addDragFunctionality() {
		if (!this.simpleBarInstance) {
			return;
		}
		const scrollElement = this.simpleBarInstance.getScrollElement();
		const carouselContainer = scrollElement.closest('.c-carousel');

		let lastX: number = 0;
		let velocity: number = 0;
		let momentumID: number | null = null;

		const momentumScroll = () => {
			if (Math.abs(velocity) > 0.1) {
				scrollElement.scrollLeft -= velocity;
				velocity *= 0.95; // Deceleration factor
				momentumID = requestAnimationFrame(momentumScroll); // Continue animation
			} else {
				cancelAnimationFrame(momentumID!); // Stop when velocity is small
			}
		};

		document.addEventListener('dragstart', (e) => {
			const target = e.target as HTMLElement;

			if (scrollElement.contains(target)) {
				if (target.tagName === 'IMG' || target.tagName === 'A' || target.tagName === 'BUTTON') {
					e.preventDefault();
				}
			}
		});

		scrollElement.addEventListener('pointerdown', (e) => {
			this.isDown = true;
			this.isDragging = false;
			if (carouselContainer) {
				carouselContainer.classList.add('is-dragging');
			}

			// Stop any ongoing momentum when starting a new drag
			cancelAnimationFrame(momentumID!);
			this.startX = e.pageX - scrollElement.offsetLeft;
			this.scrollLeft = scrollElement.scrollLeft;
			lastX = e.pageX; // Capture the starting point for velocity
		});

		scrollElement.addEventListener('pointerleave', () => {
			this.isDown = false;
			if (carouselContainer) {
				carouselContainer.classList.remove('is-dragging');
			}
		});

		scrollElement.addEventListener('pointerup', () => {
			this.isDown = false;
			if (carouselContainer) {
				carouselContainer.classList.remove('is-dragging');
			}

			setTimeout(() => {
				this.isDragging = false;
			}, 50); // Slight delay to allow click suppression

			momentumID = requestAnimationFrame(momentumScroll);
		});

		scrollElement.addEventListener('pointermove', (e) => {
			if (!this.isDown) return;
			e.preventDefault();

			this.isDragging = true;
			const x = e.pageX - scrollElement.offsetLeft;
			const walk = (x - this.startX) * 1;
			scrollElement.scrollLeft = this.scrollLeft - walk;

			// Calculate velocity (difference between current and last X position)
			velocity = e.pageX - lastX;
			lastX = e.pageX;
		});

		// Suppress click event if dragging occurred
		scrollElement.addEventListener('click', (e) => {
			if (this.isDragging) {
				e.preventDefault();
				e.stopImmediatePropagation();
			}
		});
	}

	private addArrows() {
		const arrowsHTML = `
			<button class="c-carousel__button c-carousel__button--left js-carousel-button-left" type="button" aria-label="Previous slide">
				<svg viewBox="0 0 25.08 55.69" class="c-carousel__button__icon" fill="currentColor">
					<path d="M1.61,55.69a1.57,1.57,0,0,1-1-.36,1.62,1.62,0,0,1-.23-2.27L20.92,27.85.36,2.63a1.61,1.61,0,0,1,2.5-2L25.08,27.85,2.86,55.1A1.61,1.61,0,0,1,1.61,55.69Z"/>
				</svg>
			</button>
			<button class="c-carousel__button c-carousel__button--right js-carousel-button-right" type="button" aria-label="Next slide">
				<svg viewBox="0 0 25.08 55.69" class="c-carousel__button__icon" fill="currentColor">
					<path d="M1.61,55.69a1.57,1.57,0,0,1-1-.36,1.62,1.62,0,0,1-.23-2.27L20.92,27.85.36,2.63a1.61,1.61,0,0,1,2.5-2L25.08,27.85,2.86,55.1A1.61,1.61,0,0,1,1.61,55.69Z"/>
				</svg>
			</button>

		`;
		this.containerElement.insertAdjacentHTML('afterbegin', arrowsHTML);

		this.attachArrowEvents();
		this.watchScroll();
		this.checkIfButtonsShouldBeDisabled();
	}

	private getNextScrollLeft(direction = 'next'): number {
		const children = this.containerElement.querySelectorAll('.js-scroll-carousel-item') as NodeListOf<HTMLElement>;
		const containerRect = this.containerElement.getBoundingClientRect();
		const currentScrollLeft = this.containerElement.querySelector('.simplebar-content-wrapper').scrollLeft;

		if (!children.length) {
			if (direction === 'next') {
				return currentScrollLeft + this.containerElement.clientWidth;
			} else {
				return currentScrollLeft - this.containerElement.clientWidth;
			}
		}

		if (direction === 'next') {
			for (let i = 0; i < children.length; i++) {
				const childItem = children[i];
				const childRect = childItem.getBoundingClientRect();
				// Check if the element is not fully visible on the right
				if (childRect.right > containerRect.right) {
					// Scroll this item so that it's left edge is aligned with the container's left edge
					const scrollLeft = childItem.offsetLeft;
					return scrollLeft;
				}
			}
		} else {

			for (let i = children.length - 1; i >= 0; i--) {
				const childItem = children[i];
				const childRect = childItem.getBoundingClientRect();
				// Check if the element is not fully visible on the left
				if (childRect.left < containerRect.left) {
					// Scroll this item so that it's right edge is aligned with the container's right edge
					const scrollLeft = childItem.offsetLeft - (this.containerElement.clientWidth - childItem.clientWidth);
					return scrollLeft;
				}
			}
		}

		if (direction === 'next') {
			return currentScrollLeft + this.containerElement.clientWidth;
		} else {
			return currentScrollLeft - this.containerElement.clientWidth;
		}

	}

	private attachArrowEvents() {
		this.containerElement.querySelector('.js-carousel-button-left').addEventListener('click', () => {
			this.simpleBarInstance.getScrollElement().scrollTo({
				left: this.getNextScrollLeft('prev'),
				top: 0,
				behavior: 'smooth'
			})
		});

		this.containerElement.querySelector('.js-carousel-button-right').addEventListener('click', () => {
			this.simpleBarInstance.getScrollElement().scrollTo({
				left: this.getNextScrollLeft('next'),
				top: 0,
				behavior: 'smooth'
			})
		});
	}

	private hasScrollbars() {
		return this.simpleBarInstance.getScrollElement().scrollWidth > this.simpleBarInstance.getScrollElement().clientWidth;
	}

	private checkIfButtonsShouldBeDisabled() {
		const leftButton = this.containerElement.querySelector('.js-carousel-button-left') as HTMLButtonElement;
		const rightButton = this.containerElement.querySelector('.js-carousel-button-right') as HTMLButtonElement;

		if (!this.hasScrollbars()) {
			leftButton.style.display = 'none';
			rightButton.style.display = 'none';
			return;
		} else {
			leftButton.style.display = '';
			rightButton.style.display = '';
		}

		const scrollEnd = this.simpleBarInstance.getScrollElement().scrollWidth - this.simpleBarInstance.getScrollElement().clientWidth;
		const scrollLeft = Math.ceil(this.simpleBarInstance.getScrollElement().scrollLeft);

		if (scrollLeft === 0) {
			leftButton.setAttribute('disabled', 'disabled');
		} else {
			leftButton.removeAttribute('disabled');
		}

		// if close to the end - sometimes the calculation was a few pixels off, so added a small buffer
		if (scrollLeft + 5 >= scrollEnd) {
			rightButton.setAttribute('disabled', 'disabled');
		} else {
			rightButton.removeAttribute('disabled');
		}
	}

	private onCarouselScroll() {
		const barHandle = this.carouselBar.querySelector('.simplebar-scrollbar') as HTMLElement;

		if (!barHandle) {
			return;
		}

		const barHandleLeft = barHandle.getBoundingClientRect().left - this.carouselBar.getBoundingClientRect().left;
		const barHandleWidth = barHandle.clientWidth;
		const carouselBarWidth = this.carouselBar.clientWidth;
		const placeholderElement = this.containerElement.querySelector('.js-carousel-lazyload-placeholder') as HTMLElement;
		const placeholderWidth = placeholderElement ? placeholderElement.clientWidth : 0;
		const remainingWidth = carouselBarWidth - placeholderWidth - (barHandleLeft + barHandleWidth);

		// Define a threshold for loading more products
		const nearEndThreshold = 100;

		// Check if the user has scrolled near the end of the carousel
		if (remainingWidth < nearEndThreshold) {
			if (this.lazyLoadEnabled) {
				this.loadLazyLoadItems();
			}
		}

	}

	private saveScrollPosition() {
		if (!this.blockID) {
			return;
		}
		const scrollLeft = this.simpleBarInstance.getScrollElement().scrollLeft;
		const stateToSave = {
			blockID: this.blockID,
			scrollLeft: scrollLeft,
		};
		try {
			sessionStorage.setItem(`carousel-${this.blockID}-state`, JSON.stringify(stateToSave));
		} catch (error) { }
	}

	private getBlockState() {
		const stateFromStorage = sessionStorage.getItem(`carousel-${this.blockID}-state`);
		if (!stateFromStorage) {
			return null;
		}
		return JSON.parse(stateFromStorage);
	}

	private loadLazyLoadItems() {
		const carouselItems = this.lazyLoadData[this.lazyLoadDataKey];
		if (carouselItems.length) {
			const carouselList = this.containerElement.querySelector('.c-carousel__list') as HTMLElement;
			let lazyLoadHTML = "";
			for (let i = 0; i < carouselItems.length; i++) {
				const itemTemplate = Mustache.render(this.lazyLoadTemplate, {
					[this.lazyLoadDataKey]: carouselItems[i]
				}).trim();
				lazyLoadHTML += itemTemplate;
			}
			const placeholder = this.containerElement.querySelector('.js-carousel-lazyload-placeholder') as HTMLElement;
			Helpers.removeElement(placeholder);
			carouselList.insertAdjacentHTML('beforeend', lazyLoadHTML);
			this.lazyLoadData[this.lazyLoadDataKey] = [];
			this.checkIfButtonsShouldBeDisabled();
		}
	}

	private setLazyloadPlaceholder() {
		const widthOfOneItem = this.containerElement.querySelector('.js-scroll-carousel-item');
		const numberOfLazyLoadItems = this.lazyLoadData[this.lazyLoadDataKey];
		const gap = 20;
		const totalWidth = (widthOfOneItem.clientWidth + gap) * numberOfLazyLoadItems.length;
		const backgroundSize = (widthOfOneItem.clientWidth + gap) * 2;
		const style = `width: ${totalWidth}px;
		background-image: linear-gradient(90deg, #f4f4f4 4.55%, #ffffff 4.55%, #ffffff 50%, #f4f4f4 50%, #f4f4f4 54.55%, #ffffff 54.55%, #ffffff 100%);
		background-size: ${backgroundSize}px ${backgroundSize}px;
		`;
		const carouselList = this.containerElement.querySelector('.c-carousel__list') as HTMLElement;
		carouselList.insertAdjacentHTML('beforeend', `<li style="${style}" class="c-carousel__item js-scroll-carousel-item js-carousel-lazyload-placeholder"></li>`);
	}

	private watchScroll() {
		this.simpleBarInstance.getScrollElement().addEventListener('scroll', Helpers.debounce(() => {
			this.checkIfButtonsShouldBeDisabled();
			this.saveScrollPosition();
		}, 100));
	}
}
